import { AwsRum, AwsRumConfig } from 'aws-rum-web';

// RUM instance to be used throughout the app
let awsRumInstance: AwsRum | null = null;

export function initializeAwsTracking(config: {
  AWS_RUM_APP_ID: string;
  AWS_RUM_POOL_ID: string;
  REGION: string;
}): AwsRum | null {
  if (awsRumInstance) {
    return awsRumInstance;
  }

  if (!config.AWS_RUM_APP_ID || !config.AWS_RUM_POOL_ID || !config.REGION) {
    console.warn('App Config missing required AWS RUM parameters. Skipping AWS RUM initialization.');
    return null;
  }

  let awsRum: AwsRum | null = null;
  try {
    const rumConfig: AwsRumConfig = {
      batchLimit: 10, // Send after 10 events instead of 100
      cookieAttributes: { unique: true },
      
      dispatchInterval: 5000, // Send every 5 seconds instead of 30
      identityPoolId: config.AWS_RUM_POOL_ID,
      endpoint: `https://dataplane.rum.${config.REGION}.amazonaws.com`,
      telemetries: ['http', 'errors'], // Warning: enabling performance metrics can chew through event quota.
      allowCookies: true,
      enableXRay: true,
      sessionEventLimit: 1000,
      sessionSampleRate: 1.0,
      signing: true,
    };

    const APPLICATION_ID: string = config.AWS_RUM_APP_ID;
    const APPLICATION_VERSION: string = '1.0.0';
    const APPLICATION_REGION: string = config.REGION;

    awsRum = new AwsRum(
      APPLICATION_ID,
      APPLICATION_VERSION,
      APPLICATION_REGION,
      rumConfig
    );
    
    console.log('AWS RUM initialized successfully.');
    awsRumInstance = awsRum;

    // Force dispatch before page unload
    window.addEventListener('beforeunload', () => {
      if (awsRum) {
        awsRum.dispatch();
      }
    });

    trackAwsRumEvent('proprio.tracking_init', { context: 'AWS Rum tracking initialized.' });
    
    return awsRum;
  } catch (error) {
    // Do not propogate tracing errors.
    console.error('Error initializing AWS RUM:', error);
  }
  return null;
};

// Accessor to get the initialized AwsRum instance
export function getAwsRum(): AwsRum | null {
  return awsRumInstance;
};

/**
 * Attempts to track an error using AWS RUM, if initialized.
 *
 * @param error The error object or value to be tracked.
 * @returns void
 */
export function trackAwsRumError(error: Error): void {
  const rum = getAwsRum();
  if (!rum) {
    console.info('AWS RUM is not initialized. Cannot track error in AWS RUM');
    return;
  }
  try {
    // Note: if the incoming error is not an Error object, logging it to AWS RUM may fail silently.
    rum.recordError(error as Error);
    rum.dispatch(); // Immediately dispatch to help ensure error is sent
  } catch (error) {
    // Do not propagate tracing errors.
    console.error('Error recording AWS RUM error:', error);
  }
};

/**
 * Attempts to write a custom event to AWS RUM, if initialized.
 * 
 * @param eventType An event type to categorize the event.
 * @param eventData Data object associated with the event.
 * @returns 
 */
export function trackAwsRumEvent(eventType: string, eventData: object): void {
  const rum = getAwsRum();
  if (!rum) {
    console.info('AWS RUM is not initialized. Cannot track event in AWS RUM');
    return;
  }
  try {
    rum.recordEvent(eventType, eventData);
  } catch (error) {
    // Do not propogate tracing errors.
    console.error('Error recording AWS RUM event.', error);
  }
};
